/**
 * `InMemoryBoardDataSource` — the whole table view without a backend (spec M1-B4 §5).
 *
 * It is a faithful stand-in, not a toy: one round trip returns groups with a first page,
 * summaries and totals (§5.1); paging is cursor-based with a `queryHash` guard (§5.2); filters,
 * sort, search and group-by are evaluated the way the SQL compiler would (§5.3, `filtering.ts`);
 * facets exclude the column's own rules and always offer `blank` (§5.4); "Save to this view" is
 * refused without permission (§5.6, F10) and personal state is separate (F11).
 *
 * Round 3 replaces this with a tRPC-backed implementation of the same interface. Everything in
 * `../table/` is written against the interface, never against this class.
 */
import type { BoardId, ColumnAggregation, ColumnId, GroupId, ItemId, UserId, ViewId } from '@elizmonday/shared';
import { DEFAULT_GROUP_COLOR, defaultAggregationOf, findColumnType } from '@elizmonday/shared';
import { columnWidth, getColumnUi } from '../columns/registry';
import type { CellRenderContext } from '../columns/types';
import {
  DEFAULT_TABLE_VIEW_SETTINGS,
  overlaySettings,
  pruneFilter,
  pruneSort,
  sharedSettings,
  visibleColumns,
  type FilterGroup,
  type IgnoredColumnRef,
  type TableViewSettings,
} from '../table/settings';
import { summarise } from '../table/summaries';
import {
  allBuckets,
  bucketOf,
  compareItems,
  matchesFilter,
  matchesSearch,
  orderBucketKeys,
  parseVirtualGroupId,
  virtualGroupId,
  type EvalContext,
} from './filtering';
import type {
  AccountTag,
  BoardChange,
  GroupPlacement,
  BoardColumn,
  BoardDataSource,
  BoardGroupView,
  BoardSummary,
  BoardViewPayload,
  BoardViewTab,
  CellWrite,
  CellWriteResult,
  FacetValue,
  GroupPage,
  ItemRow,
  Person,
  QueryState,
  RemovalResult,
  RestoreInput,
  SummaryValue,
  ViewPermissions,
} from './types';

/** §5.1: 50 rows per group on load, 200 on scroll, hard cap 500 (`items_page`'s own max). */
export const INITIAL_PAGE = 50;
export const PAGE = 200;
export const MAX_PAGE = 500;
/** §5.4: at most 50 facet values per column, ordered by count desc. */
export const FACET_LIMIT = 50;
/** E5: the first 50 group-by buckets render, the rest behind "show more". */
export const BUCKET_LIMIT = 50;

export interface MemoryGroup {
  id: GroupId;
  title: string;
  color: string;
  position: number;
}

export interface MemoryBoardSeed {
  board: BoardSummary;
  me: UserId;
  people: Person[];
  views: BoardViewTab[];
  columns: BoardColumn[];
  groups: MemoryGroup[];
  items: ItemRow[];
  /** M3.S1: the subitems board's columns, and its rows keyed by parent item id. */
  subitemColumns?: BoardColumn[];
  subitems?: Record<string, ItemRow[]>;
  /**
   * **P-308.** Make every subitems read and `create_subitem` fail with this sentence, the way
   * a board whose subitems board is unreachable does (`Board 6061227186 not found` on the
   * pilot). The board itself still answers — that is the whole point of the fixture.
   */
  subitemsError?: string;
  /** Saved per-view settings; missing views fall back to the defaults. */
  viewSettings?: Record<string, Partial<TableViewSettings>>;
  permissions?: Partial<ViewPermissions>;
  timezone?: string;
  /** P-122: the account tag vocabulary the `tags` column renders and picks from. */
  tags?: AccountTag[];
}

interface Cursor {
  offset: number;
  queryHash: string;
  groupId: string;
}

export class InvalidCursorError extends Error {
  constructor() {
    super('invalid_cursor');
    this.name = 'InvalidCursorError';
  }
}

export class ForbiddenError extends Error {
  constructor(message = 'forbidden') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

function encodeCursor(cursor: Cursor): string {
  return btoa(JSON.stringify(cursor));
}

function decodeCursor(raw: string): Cursor {
  try {
    const parsed = JSON.parse(atob(raw)) as Cursor;
    if (typeof parsed.offset !== 'number' || typeof parsed.queryHash !== 'string') {
      throw new InvalidCursorError();
    }
    return parsed;
  } catch {
    throw new InvalidCursorError();
  }
}

/** Cheap, stable hash of the query shape; a cursor whose hash no longer matches is rejected. */
function hashState(state: QueryState): string {
  const json = JSON.stringify([state.filter, state.sort, state.search, state.groupBy]);
  let hash = 0;
  for (let index = 0; index < json.length; index += 1) {
    hash = (hash * 31 + json.charCodeAt(index)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * The fixture's subitems board id: a stable derivation of the parent's, so the grid can name
 * the board its nested `+` adds a column to without the fixture inventing an id per call.
 */
function subitemsBoardIdOf(boardId: BoardId): BoardId {
  return `${boardId}-subitems` as BoardId;
}

export class InMemoryBoardDataSource implements BoardDataSource {
  /** Every read comes from the seed — nothing here ever calls the api (T-8d). */
  readonly offline = true;
  private readonly seed: MemoryBoardSeed;
  private items: ItemRow[];
  private columns: BoardColumn[];
  private groups: MemoryGroup[];
  private readonly saved = new Map<string, TableViewSettings>();
  private readonly personal = new Map<string, Partial<TableViewSettings>>();
  private readonly collapsed = new Map<string, Set<string>>();
  private readonly listeners = new Set<(change: BoardChange) => void>();
  /** `action_record_uuid -> the rows that action removed` — the undo toast's memory (P-050). */
  private readonly removed = new Map<string, ItemRow[]>();
  /** P-087: soft-deleted columns, so `restoreColumn` puts one back where it was. */
  private readonly deletedColumns = new Map<string, { column: BoardColumn; at: number }>();
  private sequence = 0;
  /** M3.S1: the subitems board, modelled exactly as the server sends it. */
  private readonly subitemColumns: BoardColumn[];
  private readonly subitems: Record<string, ItemRow[]>;

  constructor(seed: MemoryBoardSeed) {
    this.seed = seed;
    this.items = [...seed.items];
    this.columns = [...seed.columns];
    this.groups = [...seed.groups];
    this.subitemColumns = [...(seed.subitemColumns ?? [])];
    this.subitems = Object.fromEntries(
      Object.entries(seed.subitems ?? {}).map(([parentId, rows]) => [parentId, [...rows]]),
    );
    for (const view of seed.views) {
      this.saved.set(view.id, {
        ...DEFAULT_TABLE_VIEW_SETTINGS,
        ...(seed.viewSettings?.[view.id] ?? {}),
      });
    }
  }

  /* ── internals ──────────────────────────────────────────────────────────── */

  private get permissions(): ViewPermissions {
    return {
      canSaveView: true,
      canEditStructure: true,
      canEditContent: true,
      readOnlyColumnIds: this.columns
        .filter((column) => findColumnType(column.type)?.writable === false)
        .map((column) => column.id),
      ...this.seed.permissions,
    };
  }

  private renderContext(): CellRenderContext {
    return {
      people: this.seed.people,
      tags: this.seed.tags ?? [],
      createTag: (name: string) => this.createTag({ boardId: this.seed.board.id, name }),
      columns: this.columns,
      search: '',
      timezone: this.seed.timezone ?? 'UTC',
    };
  }

  private evalContext(): EvalContext {
    return {
      columns: new Map(this.columns.map((column) => [column.id as string, column])),
      render: this.renderContext(),
      now: new Date(),
    };
  }

  private settingsFor(viewId: ViewId, override?: Partial<QueryState>): {
    saved: TableViewSettings;
    effective: TableViewSettings;
    state: QueryState;
  } {
    const known = new Set(this.columns.map((column) => column.id as string));
    const savedRaw = this.saved.get(viewId) ?? DEFAULT_TABLE_VIEW_SETTINGS;
    // E1 / E15: a rule naming a deleted column is dropped on read, never on write.
    const saved: TableViewSettings = {
      ...savedRaw,
      filter: pruneFilter(savedRaw.filter, known).filter,
      sort: pruneSort(savedRaw.sort, known),
    };
    const effectiveRaw = overlaySettings(saved, this.personal.get(viewId));
    const effective: TableViewSettings = {
      ...effectiveRaw,
      filter: pruneFilter(effectiveRaw.filter, known).filter,
      sort: pruneSort(effectiveRaw.sort, known),
      collapsedGroupIds: [...(this.collapsed.get(viewId) ?? new Set<string>())] as GroupId[],
    };
    const state: QueryState = {
      filter: override?.filter !== undefined ? override.filter : effective.filter,
      sort: override?.sort ?? effective.sort,
      search: override?.search ?? effective.search,
      groupBy: override?.groupBy !== undefined ? override.groupBy : effective.groupBy,
      hiddenColumnIds: override?.hiddenColumnIds ?? effective.hiddenColumnIds,
      collapsedGroupIds: override?.collapsedGroupIds ?? effective.collapsedGroupIds,
    };
    return { saved, effective, state };
  }

  /** The filtered, searched, sorted item list — the "result set" every read works from. */
  private resultSet(state: QueryState): ItemRow[] {
    const context = this.evalContext();
    const settings: TableViewSettings = { ...DEFAULT_TABLE_VIEW_SETTINGS, ...state };
    const { pinned, scrolling } = visibleColumns(this.columns, settings, 'name' as ColumnId);
    const visible = [...pinned, ...scrolling];
    return this.items
      .filter(
        (item) =>
          matchesFilter(item, state.filter, context) &&
          matchesSearch(item, state.search, visible, context),
      )
      .sort((a, b) => compareItems(a, b, state.sort, context));
  }

  private summariesFor(items: readonly ItemRow[]): Record<string, SummaryValue> {
    const context = this.renderContext();
    const out: Record<string, SummaryValue> = {};
    for (const column of this.columns) {
      if (column.id === 'name') continue;
      const cells = items.map((item) => item.values[column.id] ?? null);
      const value = summarise(column, cells, context);
      if (value) out[column.id] = value;
    }
    return out;
  }

  private emit(change: BoardChange): void {
    for (const listener of this.listeners) listener(change);
  }

  /* ── reads ──────────────────────────────────────────────────────────────── */

  async load(input: {
    boardId: BoardId;
    viewId: ViewId;
    state?: Partial<QueryState>;
  }): Promise<BoardViewPayload> {
    const { saved, effective, state } = this.settingsFor(input.viewId, input.state);
    const rows = this.resultSet(state);
    const collapsed = new Set(state.collapsedGroupIds.map(String));
    const queryHash = hashState(state);

    const groups: BoardGroupView[] = state.groupBy
      ? this.virtualGroups(rows, state, collapsed, queryHash)
      : this.realGroups(rows, collapsed, queryHash);

    return {
      board: this.seed.board,
      columns: [...this.columns],
      views: this.seed.views,
      view: { id: input.viewId, settings: saved, effectiveSettings: effective },
      groups,
      totals: { items: this.items.length, filtered: rows.length },
      /*
       * **P-268.** This source has dropped rules naming a deleted column since E1/E15; it now
       * *says* which, exactly as `boards.read` does, so the store's own pruning is exercised
       * by every fixture-driven test and by the `/__ui` harness rather than only against the api.
       */
      ignored: this.staleRefsIn(input.viewId, input.state),
      people: this.seed.people,
      tags: this.seed.tags ?? [],
      permissions: this.permissions,
      me: this.seed.me,
    };
  }

  /**
   * Every column reference in the saved settings, the personal overlay and the caller's own
   * query state that names a column this board does not have — P-268's `ignored`.
   */
  private staleRefsIn(viewId: ViewId, override?: Partial<QueryState>): IgnoredColumnRef[] {
    const known = new Set(this.columns.map((column) => String(column.id)));
    const out = new Map<string, IgnoredColumnRef>();
    const note = (kind: IgnoredColumnRef['kind'], columnId: string) => {
      if (!known.has(columnId)) out.set(`${kind}:${columnId}`, { kind, columnId });
    };
    const scanFilter = (group: FilterGroup | null | undefined) => {
      if (!group) return;
      for (const rule of group.rules) note('filter', String(rule.columnId));
      for (const child of group.groups) scanFilter(child);
    };
    const scan = (settings: Partial<TableViewSettings> | undefined) => {
      if (!settings) return;
      for (const rule of settings.sort ?? []) note('sort', String(rule.columnId));
      scanFilter(settings.filter);
      for (const id of settings.hiddenColumnIds ?? []) note('hiddenColumns', String(id));
      for (const id of settings.pinnedColumnIds ?? []) note('pinnedColumns', String(id));
      for (const id of settings.wrappedColumnIds ?? []) note('wrappedColumns', String(id));
      for (const id of settings.columnOrder ?? []) note('columnOrder', String(id));
      if (settings.groupBy) note('groupBy', String(settings.groupBy.columnId));
    };
    scan(this.saved.get(viewId));
    scan(this.personal.get(viewId));
    scan(override as Partial<TableViewSettings> | undefined);
    return [...out.values()];
  }

  private realGroups(
    rows: readonly ItemRow[],
    collapsed: ReadonlySet<string>,
    queryHash: string,
  ): BoardGroupView[] {
    return [...this.groups]
      .sort((a, b) => a.position - b.position)
      .map((group) => {
        const members = rows.filter((item) => item.groupId === group.id);
        const isCollapsed = collapsed.has(group.id);
        // A collapsed group ships its header, its counters and its summaries but zero rows.
        const page = isCollapsed
          ? { items: [], cursor: null, total: members.length }
          : this.pageOf(members, group.id, 0, INITIAL_PAGE, queryHash);
        return {
          id: group.id,
          title: group.title,
          color: group.color,
          position: group.position,
          collapsed: isCollapsed,
          virtual: false,
          page,
          summaries: this.summariesFor(members),
          subitemCount: members.reduce((total, item) => total + item.subitemIds.length, 0),
        };
      });
  }

  private virtualGroups(
    rows: readonly ItemRow[],
    state: QueryState,
    collapsed: ReadonlySet<string>,
    queryHash: string,
  ): BoardGroupView[] {
    const groupBy = state.groupBy!;
    const column = this.columns.find((entry) => entry.id === groupBy.columnId);
    if (!column) return this.realGroups(rows, collapsed, queryHash);
    const context = this.evalContext();

    const buckets = new Map<string, { label: string; color: string | null; items: ItemRow[] }>();
    if (groupBy.showEmptyGroups) {
      for (const bucket of allBuckets(column, this.items, context)) {
        buckets.set(bucket.key, { label: bucket.label, color: bucket.color, items: [] });
      }
    }
    for (const item of rows) {
      const bucket = bucketOf(item, column, context);
      const existing = buckets.get(bucket.key);
      if (existing) existing.items.push(item);
      else buckets.set(bucket.key, { label: bucket.label, color: bucket.color, items: [item] });
    }

    // The server's order, not a string sort: declared vocabulary, then first appearance,
    // `Blank` last (`filtering.ts` `orderBucketKeys`).
    const ordered = orderBucketKeys(column, [...buckets.keys()], groupBy.direction).map(
      (key) => [key, buckets.get(key)!] as const,
    );

    return ordered.slice(0, BUCKET_LIMIT).map(([key, bucket], index) => {
      const id = virtualGroupId(groupBy.columnId, key) as GroupId;
      const isCollapsed = collapsed.has(id);
      return {
        id,
        title: bucket.label,
        // Buckets without a colour of their own inherit the neutral grey monday uses.
        color: bucket.color ?? 'var(--empty-status-color, #c4c4c4)',
        position: index,
        collapsed: isCollapsed,
        virtual: true,
        page: isCollapsed
          ? { items: [], cursor: null, total: bucket.items.length }
          : this.pageOf(bucket.items, id, 0, INITIAL_PAGE, queryHash),
        summaries: this.summariesFor(bucket.items),
        subitemCount: bucket.items.reduce((total, item) => total + item.subitemIds.length, 0),
      };
    });
  }

  private pageOf(
    members: readonly ItemRow[],
    groupId: string,
    offset: number,
    limit: number,
    queryHash: string,
  ): GroupPage {
    const slice = members.slice(offset, offset + limit);
    const next = offset + slice.length;
    return {
      items: slice,
      cursor: next < members.length ? encodeCursor({ offset: next, queryHash, groupId }) : null,
      total: members.length,
    };
  }

  async page(input: {
    boardId: BoardId;
    viewId: ViewId;
    groupId: GroupId;
    cursor: string | null;
    limit?: number;
    state?: Partial<QueryState>;
  }): Promise<GroupPage> {
    const { state } = this.settingsFor(input.viewId, input.state);
    const queryHash = hashState(state);
    const cursor = input.cursor ? decodeCursor(input.cursor) : { offset: 0, queryHash, groupId: input.groupId };
    // §5.2: a cursor whose `queryHash` no longer matches is rejected, never silently reused.
    if (cursor.queryHash !== queryHash || cursor.groupId !== input.groupId) throw new InvalidCursorError();

    const rows = this.resultSet(state);
    const members = this.membersOf(rows, input.groupId, state);
    const limit = Math.min(input.limit ?? PAGE, MAX_PAGE);
    return this.pageOf(members, input.groupId, cursor.offset, limit, queryHash);
  }

  private membersOf(rows: readonly ItemRow[], groupId: string, state: QueryState): ItemRow[] {
    const virtual = parseVirtualGroupId(groupId);
    if (!virtual || !state.groupBy) return rows.filter((item) => item.groupId === groupId);
    const column = this.columns.find((entry) => entry.id === virtual.columnId);
    if (!column) return [];
    const context = this.evalContext();
    return rows.filter((item) => bucketOf(item, column, context).key === virtual.bucketKey);
  }

  async facets(input: {
    boardId: BoardId;
    viewId: ViewId;
    columnIds?: readonly ColumnId[];
    state?: Partial<QueryState>;
  }): Promise<Record<string, FacetValue[]>> {
    const { state } = this.settingsFor(input.viewId, input.state);
    const context = this.evalContext();
    // F7: a hidden column leaves the quick-filter pane as well as the grid.
    const hidden = new Set(state.hiddenColumnIds.map(String));
    const wanted = input.columnIds
      ? this.columns.filter((column) => input.columnIds!.includes(column.id))
      : this.columns.filter(
          (column) =>
            !hidden.has(String(column.id)) &&
            (findColumnType(column.type)?.filterOperators.length ?? 0) > 0,
        );

    const out: Record<string, FacetValue[]> = {};
    for (const column of wanted) {
      // §5.4: the counts exclude this column's own rules, so the pane behaves like every
      // faceted UI — picking a second value in the same column widens, not narrows.
      const withoutOwn = stripColumn(state.filter, column.id);
      const rows = this.items.filter(
        (item) =>
          matchesFilter(item, withoutOwn, context) &&
          matchesSearch(item, state.search, this.columns, context),
      );
      const counts = new Map<string, FacetValue>();
      let blank = 0;
      for (const item of rows) {
        const value = column.id === 'name' ? item.name : (item.values[column.id] ?? null);
        const buckets = getColumnUi(column.type).bucketsOf(value, column.settings, context.render);
        if (buckets.length === 0) {
          blank += 1;
          continue;
        }
        for (const bucket of buckets) {
          const key = String(bucket.key);
          const existing = counts.get(key);
          if (existing) counts.set(key, { ...existing, count: existing.count + 1 });
          else counts.set(key, { value: bucket.key, label: bucket.label, count: 1 });
        }
      }
      const values = [...counts.values()].sort((a, b) => b.count - a.count).slice(0, FACET_LIMIT);
      // The captured pane always offers a `blank` bucket `[ui:library/table-06-filter-pane.png]`.
      values.push({ value: '$$$blank$$$', label: 'blank', count: blank });
      out[column.id] = values;
    }
    return out;
  }

  /* ── writes ─────────────────────────────────────────────────────────────── */

  async setCellValues(input: {
    boardId: BoardId;
    writes: readonly CellWrite[];
    isUndo?: boolean;
    /** Accepted and ignored: the fixture source has no socket to reconcile against. */
    mutationId?: string;
  }): Promise<CellWriteResult> {
    const readOnly = new Set(this.permissions.readOnlyColumnIds.map(String));
    const written: CellWrite[] = [];
    const skipped: { itemId: ItemId; columnId: ColumnId; reason: string }[] = [];
    const undo: CellWrite[] = [];

    /*
     * **P-326.** The subitems board is a board of its own, as on the server: its cells are
     * written against **its** columns and **its** rows, and a subitem named on the parent
     * board is an unknown item there — which is exactly the refusal the grid used to get.
     */
    if (String(input.boardId) === String(subitemsBoardIdOf(this.seed.board.id))) {
      for (const write of input.writes) {
        const column = this.subitemColumns.find((entry) => entry.id === write.columnId);
        const parentId = Object.keys(this.subitems).find((key) =>
          (this.subitems[key] ?? []).some((row) => String(row.id) === String(write.itemId)),
        );
        if (!column || parentId === undefined) {
          skipped.push({ ...write, reason: column ? 'unknown item' : 'unknown column' });
          continue;
        }
        const rows = this.subitems[parentId]!;
        const index = rows.findIndex((row) => String(row.id) === String(write.itemId));
        const row = rows[index]!;
        const previous = row.values[write.columnId] ?? null;
        if (findColumnType(column.type)?.isEqual(previous, write.value, column.settings)) continue;
        undo.push({ itemId: write.itemId, columnId: write.columnId, value: previous });
        this.subitems[parentId] = rows.map((entry, at) =>
          at === index ? { ...entry, values: { ...entry.values, [write.columnId]: write.value } } : entry,
        );
        written.push(write);
      }
      if (written.length > 0) this.emit({ kind: 'cells', writes: written });
      return { written, skipped, undo };
    }

    for (const write of input.writes) {
      const column = this.columns.find((entry) => entry.id === write.columnId);
      if (!column) {
        skipped.push({ ...write, reason: 'unknown column' });
        continue;
      }
      if (readOnly.has(String(write.columnId))) {
        skipped.push({ ...write, reason: 'read-only' });
        continue;
      }
      const index = this.items.findIndex((item) => item.id === write.itemId);
      if (index < 0) {
        skipped.push({ ...write, reason: 'unknown item' });
        continue;
      }
      const item = this.items[index]!;
      const previous = item.values[write.columnId] ?? null;
      const shared = findColumnType(column.type);
      // The no-op gate of B5 §5.1: an equal write produces no event and no activity row.
      if (shared?.isEqual(previous, write.value, column.settings)) continue;
      undo.push({ itemId: write.itemId, columnId: write.columnId, value: previous });
      this.items[index] = {
        ...item,
        values: { ...item.values, [write.columnId]: write.value },
      };
      written.push(write);
    }

    if (written.length > 0) this.emit({ kind: 'cells', writes: written });
    return { written, skipped, undo };
  }

  /**
   * `create_or_get_tag` against the in-memory vocabulary (P-122): the same name always answers
   * the same tag, which is the half of monday's verb that makes the picker idempotent.
   */
  async createTag(input: { boardId: BoardId; name: string }): Promise<AccountTag> {
    const name = input.name.trim();
    const existing = (this.seed.tags ?? []).find(
      (tag) => tag.name.toLowerCase() === name.toLowerCase(),
    );
    if (existing) return existing;
    const id = Math.max(0, ...(this.seed.tags ?? []).map((tag) => tag.id)) + 1;
    const created: AccountTag = { id, name };
    this.seed.tags = [...(this.seed.tags ?? []), created];
    this.emit({ kind: 'items' });
    return created;
  }

  async renameItem(input: { boardId: BoardId; itemId: ItemId; name: string }): Promise<ItemRow> {
    const index = this.items.findIndex((item) => item.id === input.itemId);
    if (index < 0) throw new Error('unknown item');
    const next = { ...this.items[index]!, name: input.name };
    this.items[index] = next;
    this.emit({ kind: 'items' });
    return next;
  }

  async createItem(input: {
    boardId: BoardId;
    groupId: GroupId;
    name: string;
    placement?: 'top' | 'bottom' | { afterItemId: ItemId };
  }): Promise<ItemRow> {
    const members = this.items.filter((item) => item.groupId === input.groupId);
    const placement = input.placement ?? 'bottom';
    let position: number;
    if (placement === 'top') position = (members[0]?.position ?? 1) - 1;
    else if (placement === 'bottom') position = (members[members.length - 1]?.position ?? -1) + 1;
    else {
      const anchor = this.items.find((item) => item.id === placement.afterItemId);
      position = (anchor?.position ?? members.length) + 0.5;
    }
    this.sequence += 1;
    const row: ItemRow = {
      id: `9${Date.now()}${this.sequence}` as ItemId,
      name: input.name,
      groupId: input.groupId,
      position,
      updatesCount: 0,
      subitemIds: [],
      values: {},
    };
    this.items.push(row);
    this.emit({ kind: 'items' });
    return row;
  }

  /* ── subitems (M3.S1) ──────────────────────────────────────────────────── */

  /**
   * The fixture models the subitems board as four columns and a flat list of rows keyed by
   * parent — the same shape the server sends — so the nested table runs with no api at all,
   * exactly as the rest of B4 does. The columns mirror what monday seeds `[sub§2]`.
   */
  async subitemsOf(input: {
    boardId: BoardId;
    parentItemIds: readonly ItemId[];
  }): Promise<{
    boardId: BoardId | null;
    columns: readonly BoardColumn[];
    byParent: Record<string, readonly ItemRow[]>;
  }> {
    // P-308: the refusal an unreachable subitems board answers with.
    if (this.seed.subitemsError !== undefined) throw new Error(this.seed.subitemsError);
    const wanted = new Set(input.parentItemIds.map(String));
    const byParent: Record<string, ItemRow[]> = {};
    for (const [parentId, rows] of Object.entries(this.subitems)) {
      if (!wanted.has(parentId)) continue;
      byParent[parentId] = [...rows].sort((a, b) => a.position - b.position);
    }
    /** P-111: the fixture's subitems live on a board of their own, as the server's do. */
    return { boardId: subitemsBoardIdOf(input.boardId), columns: this.subitemColumns, byParent };
  }

  async createSubitem(input: {
    boardId: BoardId;
    parentItemId: ItemId;
    name: string;
  }): Promise<ItemRow> {
    if (this.seed.subitemsError !== undefined) throw new Error(this.seed.subitemsError);
    const siblings = this.subitems[String(input.parentItemId)] ?? [];
    this.sequence += 1;
    const row: ItemRow = {
      id: `8${Date.now()}${this.sequence}` as ItemId,
      name: input.name,
      groupId: 'topics' as GroupId,
      position: (siblings[siblings.length - 1]?.position ?? -1) + 1,
      updatesCount: 0,
      subitemIds: [],
      values: {},
    };
    this.subitems[String(input.parentItemId)] = [...siblings, row];
    const parentIndex = this.items.findIndex((item) => item.id === input.parentItemId);
    if (parentIndex >= 0) {
      const parent = this.items[parentIndex]!;
      this.items[parentIndex] = { ...parent, subitemIds: [...parent.subitemIds, row.id] };
    }
    this.emit({ kind: 'items' });
    return row;
  }

  /**
   * P-050: the removed rows are kept aside under the action's own id, exactly as the server
   * keeps an `item_deletions` row, so `restoreItems` can put **that** set back — with its
   * `position`, which is what makes the row reappear where it was rather than at the bottom.
   */
  async deleteItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
  }): Promise<RemovalResult> {
    return this.removeItems(input.boardId, input.itemIds, 'delete');
  }

  async archiveItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
  }): Promise<RemovalResult> {
    // Archived rows leave the board view exactly like deleted ones do; the difference is
    // server-side state this source does not model.
    return this.removeItems(input.boardId, input.itemIds, 'archive');
  }

  /**
   * **P-304.** The board is read, not assumed: a removal aimed at the **subitems** board takes
   * the rows out of the nested lists and off their parent's `subitemIds`, which is what makes
   * the count bubble fall. The fixture modelled the real defect faithfully — a subitem sent to
   * `deleteItems` with the parent board's id matched nothing and stayed on screen.
   */
  private removeItems(
    boardId: BoardId,
    itemIds: readonly ItemId[],
    kind: 'delete' | 'archive',
  ): RemovalResult {
    const wanted = new Set(itemIds.map(String));
    this.sequence += 1;
    const actionRecordUuid = `mem-${kind}-${this.sequence}`;

    if (String(boardId) === String(subitemsBoardIdOf(this.seed.board.id))) {
      const removed: ItemRow[] = [];
      for (const [parentId, rows] of Object.entries(this.subitems)) {
        const going = rows.filter((row) => wanted.has(String(row.id)));
        if (going.length === 0) continue;
        removed.push(...going.map((row) => ({ ...row, parentItemId: parentId })));
        this.subitems[parentId] = rows.filter((row) => !wanted.has(String(row.id)));
        this.detachSubitemIds(parentId, wanted);
      }
      this.removed.set(actionRecordUuid, removed);
      this.emit({ kind: 'items' });
      return { itemIds: removed.map((item) => item.id), actionRecordUuid };
    }

    const removed = this.items.filter((item) => wanted.has(String(item.id)));
    this.items = this.items.filter((item) => !wanted.has(String(item.id)));
    this.removed.set(actionRecordUuid, removed.map((item) => ({ ...item })));
    this.emit({ kind: 'items' });
    return { itemIds: removed.map((item) => item.id), actionRecordUuid };
  }

  /** Takes ids off a parent's `subitemIds`, which is where the count bubble is read from. */
  private detachSubitemIds(parentId: string, ids: ReadonlySet<string>): void {
    const index = this.items.findIndex((item) => String(item.id) === parentId);
    const parent = index < 0 ? null : this.items[index];
    if (!parent) return;
    this.items[index] = {
      ...parent,
      subitemIds: parent.subitemIds.filter((id) => !ids.has(String(id))),
    };
  }

  async restoreItems(input: RestoreInput): Promise<void> {
    this.putBack(input);
  }

  async unarchiveItems(input: RestoreInput): Promise<void> {
    this.putBack(input);
  }

  /**
   * The undo. With an action id it restores that whole action (the server's `undoBulk` path);
   * without one it falls back to the ids, the way a per-item `items.restore` loop would.
   */
  private putBack(input: RestoreInput): void {
    const uuid = input.actionRecordUuid ?? null;
    const pool =
      uuid !== null
        ? (this.removed.get(uuid) ?? [])
        : [...this.removed.values()].flat().filter((item) => input.itemIds.some((id) => String(id) === String(item.id)));
    if (pool.length === 0) return;
    const back = new Set(pool.map((item) => String(item.id)));
    const live = new Set(this.items.map((item) => String(item.id)));
    for (const item of pool) {
      /*
       * P-304: a row removed from a nested table goes back **into that nested table**, under
       * the parent it was taken from, and on to the parent's `subitemIds` so the count bubble
       * comes back with it. `parentItemId` is set only on rows this source took off a parent.
       */
      const parentId = (item as ItemRow & { parentItemId?: string }).parentItemId;
      if (parentId !== undefined) {
        const siblings = this.subitems[parentId] ?? [];
        if (!siblings.some((row) => String(row.id) === String(item.id))) {
          const { parentItemId: _dropped, ...row } = item as ItemRow & { parentItemId?: string };
          this.subitems[parentId] = [...siblings, row as ItemRow].sort((a, b) => a.position - b.position);
          const index = this.items.findIndex((candidate) => String(candidate.id) === parentId);
          const parent = index < 0 ? null : this.items[index];
          if (parent && !parent.subitemIds.some((id) => String(id) === String(item.id))) {
            this.items[index] = { ...parent, subitemIds: [...parent.subitemIds, item.id] };
          }
        }
        continue;
      }
      if (!live.has(String(item.id))) this.items.push({ ...item });
    }
    // Position order is what the readers sort on, so the rows land back between their neighbours.
    this.items.sort((a, b) => a.position - b.position);
    if (uuid !== null) this.removed.delete(uuid);
    else {
      for (const [key, rows] of this.removed) {
        const left = rows.filter((item) => !back.has(String(item.id)));
        if (left.length === 0) this.removed.delete(key);
        else this.removed.set(key, left);
      }
    }
    this.emit({ kind: 'items' });
  }

  async duplicateItems(input: { boardId: BoardId; itemIds: readonly ItemId[] }): Promise<void> {
    // P-304: a subitem is duplicated beside its sibling, on the subitems board.
    if (String(input.boardId) === String(subitemsBoardIdOf(this.seed.board.id))) {
      for (const subitemId of input.itemIds) {
        const parentId = Object.keys(this.subitems).find((key) =>
          (this.subitems[key] ?? []).some((row) => String(row.id) === String(subitemId)),
        );
        const rows = parentId === undefined ? [] : (this.subitems[parentId] ?? []);
        const original = rows.find((row) => String(row.id) === String(subitemId));
        if (parentId === undefined || !original) continue;
        this.sequence += 1;
        const copy: ItemRow = {
          ...original,
          id: `9${Date.now()}${this.sequence}` as ItemId,
          name: `${original.name} (copy)`,
          position: original.position + 0.5,
        };
        this.subitems[parentId] = [...rows, copy].sort((a, b) => a.position - b.position);
        const index = this.items.findIndex((item) => String(item.id) === parentId);
        const parent = index < 0 ? null : this.items[index];
        if (parent) this.items[index] = { ...parent, subitemIds: [...parent.subitemIds, copy.id] };
      }
      this.emit({ kind: 'items' });
      return;
    }
    for (const itemId of input.itemIds) {
      const source = this.items.find((item) => item.id === itemId);
      if (!source) continue;
      this.sequence += 1;
      const copyId = `9${Date.now()}${this.sequence}` as ItemId;
      /*
       * **P-326**, as the server does it (`items.ts#duplicateSubitemsUnder`, captured in
       * `discovery/ui-reference/subitems/_duplicate-readback.json`): the copy gets its **own**
       * subitems — new ids, the same names, the same cells, in the same order. It used to
       * inherit the source's `subitemIds`, so both rows counted one set of subitems.
       */
      const clones = (this.subitems[String(source.id)] ?? []).map((row) => {
        this.sequence += 1;
        return { ...row, id: `8${Date.now()}${this.sequence}` as ItemId, values: { ...row.values } };
      });
      if (clones.length > 0) this.subitems[String(copyId)] = clones;
      this.items.push({
        ...source,
        id: copyId,
        name: `${source.name} (copy)`,
        position: source.position + 0.5,
        subitemIds: clones.map((row) => row.id),
      });
    }
    this.emit({ kind: 'items' });
  }

  async moveItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    groupId: GroupId;
  }): Promise<void> {
    const moving = new Set(input.itemIds.map(String));
    const target = this.items.filter((item) => item.groupId === input.groupId);
    let position = (target[target.length - 1]?.position ?? -1) + 1;
    this.items = this.items.map((item) =>
      moving.has(String(item.id)) ? { ...item, groupId: input.groupId, position: position++ } : item,
    );
    this.emit({ kind: 'items' });
  }

  /**
   * The fixture has exactly one board, so "move to another board" can only be modelled as a
   * removal — which is what the real move looks like from *this* board's point of view.
   */
  async moveItemsToBoard(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    targetBoardId: BoardId;
    targetGroupId: GroupId;
  }): Promise<void> {
    const moving = new Set(input.itemIds.map(String));
    this.items = this.items.filter((item) => !moving.has(String(item.id)));
    this.emit({ kind: 'items' });
  }

  async convertItemsToSubitems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    parentItemId: ItemId;
  }): Promise<void> {
    const moving = new Set(input.itemIds.map(String));
    this.items = this.items.filter((item) => !moving.has(String(item.id)));
    this.emit({ kind: 'items' });
  }

  /** P-304 — `Move to Item ▸`: the row leaves one nested table and joins another. */
  async moveSubitemToParent(input: {
    boardId: BoardId;
    subitemId: ItemId;
    parentItemId: ItemId;
  }): Promise<void> {
    const wanted = new Set([String(input.subitemId)]);
    let moved: ItemRow | null = null;
    for (const [parentId, rows] of Object.entries(this.subitems)) {
      const row = rows.find((candidate) => wanted.has(String(candidate.id)));
      if (!row) continue;
      moved = row;
      this.subitems[parentId] = rows.filter((candidate) => !wanted.has(String(candidate.id)));
      this.detachSubitemIds(parentId, wanted);
    }
    if (!moved) return;
    const target = String(input.parentItemId);
    const siblings = this.subitems[target] ?? [];
    this.subitems[target] = [...siblings, { ...moved, position: (siblings.at(-1)?.position ?? -1) + 1 }];
    const index = this.items.findIndex((item) => String(item.id) === target);
    const parent = index < 0 ? null : this.items[index];
    if (parent) this.items[index] = { ...parent, subitemIds: [...parent.subitemIds, moved.id] };
    this.emit({ kind: 'items' });
  }

  /** P-304 — `Convert to item`: the row leaves the nested table for the parent's own board. */
  async convertSubitemToItem(input: { boardId: BoardId; subitemId: ItemId }): Promise<void> {
    const wanted = new Set([String(input.subitemId)]);
    for (const [parentId, rows] of Object.entries(this.subitems)) {
      const row = rows.find((candidate) => wanted.has(String(candidate.id)));
      if (!row) continue;
      this.subitems[parentId] = rows.filter((candidate) => !wanted.has(String(candidate.id)));
      this.detachSubitemIds(parentId, wanted);
      const parent = this.items.find((item) => String(item.id) === parentId);
      this.items.push({
        ...row,
        groupId: parent?.groupId ?? this.groups[0]?.id ?? row.groupId,
        position: this.items.length,
        subitemIds: [],
      });
    }
    this.emit({ kind: 'items' });
  }

  async setGroupCollapsed(input: {
    boardId: BoardId;
    viewId: ViewId;
    groupId: GroupId | 'all';
    collapsed: boolean;
  }): Promise<void> {
    const set = this.collapsed.get(input.viewId) ?? new Set<string>();
    if (input.groupId === 'all') {
      set.clear();
      if (input.collapsed) for (const group of this.groups) set.add(group.id);
    } else if (input.collapsed) set.add(input.groupId);
    else set.delete(input.groupId);
    this.collapsed.set(input.viewId, set);
    this.emit({ kind: 'view' });
  }

  /* ── group lifecycle (`[B2§5]`) ─────────────────────────────────────────── */

  /** Where a placement lands among the current groups. */
  private groupIndexFor(placement: GroupPlacement | undefined): number {
    if (placement === undefined || placement === 'bottom') return this.groups.length;
    if (placement === 'top') return 0;
    const at = this.groups.findIndex((group) => String(group.id) === placement.relativeTo);
    if (at < 0) return this.groups.length;
    return placement.method === 'before_at' ? at : at + 1;
  }

  async createGroup(input: { boardId: BoardId; title?: string; placement?: GroupPlacement }): Promise<void> {
    this.sequence += 1;
    const id = `group_m${String(this.sequence)}${Date.now().toString(36)}` as GroupId;
    const index = this.groupIndexFor(input.placement);
    this.groups.splice(index, 0, {
      id,
      title: input.title ?? 'New Group',
      // B2 §5: monday's default group colour, owned by `packages/shared`.
      color: DEFAULT_GROUP_COLOR,
      position: index,
    });
    this.renumberGroups();
    this.emit({ kind: 'items' });
  }

  async renameGroup(input: { boardId: BoardId; groupId: GroupId; title: string }): Promise<void> {
    const group = this.groups.find((entry) => entry.id === input.groupId);
    if (group) group.title = input.title;
    this.emit({ kind: 'items' });
  }

  async setGroupColor(input: { boardId: BoardId; groupId: GroupId; color: string }): Promise<void> {
    const group = this.groups.find((entry) => entry.id === input.groupId);
    if (group) group.color = input.color;
    this.emit({ kind: 'items' });
  }

  /** `withUpdates` (P-058) is accepted and ignored: the fixture source carries no updates. */
  async duplicateGroup(input: {
    boardId: BoardId;
    groupId: GroupId;
    withItems: boolean;
    withUpdates?: boolean;
  }): Promise<void> {
    const at = this.groups.findIndex((group) => group.id === input.groupId);
    if (at < 0) return;
    const source = this.groups[at]!;
    this.sequence += 1;
    const id = `duplicate_of_${String(source.id)}_${String(this.sequence)}` as GroupId;
    this.groups.splice(at + 1, 0, { ...source, id, title: `Duplicate of ${source.title}` });
    if (input.withItems) {
      const copies = this.items
        .filter((item) => item.groupId === source.id)
        .map((item, index) => ({
          ...item,
          id: `9${Date.now().toString()}${String(index)}` as ItemId,
          groupId: id,
        }));
      this.items.push(...copies);
    }
    this.renumberGroups();
    this.emit({ kind: 'items' });
  }

  async moveGroup(input: { boardId: BoardId; groupId: GroupId; placement: GroupPlacement }): Promise<void> {
    const from = this.groups.findIndex((group) => group.id === input.groupId);
    if (from < 0) return;
    const [group] = this.groups.splice(from, 1);
    const to = Math.min(this.groupIndexFor(input.placement), this.groups.length);
    this.groups.splice(to, 0, group!);
    this.renumberGroups();
    this.emit({ kind: 'items' });
  }

  /** Archiving and deleting both take the group and its items out of the view (`[B2§5.6]`). */
  async archiveGroup(input: { boardId: BoardId; groupId: GroupId }): Promise<void> {
    await this.deleteGroup(input);
  }

  async deleteGroup(input: { boardId: BoardId; groupId: GroupId }): Promise<void> {
    this.groups = this.groups.filter((group) => group.id !== input.groupId);
    this.items = this.items.filter((item) => item.groupId !== input.groupId);
    this.renumberGroups();
    this.emit({ kind: 'items' });
  }

  private renumberGroups(): void {
    this.groups = this.groups.map((group, index) => ({ ...group, position: index }));
  }

  async reorderItem(input: {
    boardId: BoardId;
    itemId: ItemId;
    groupId: GroupId;
    afterItemId: ItemId | null;
    fromGroupId?: GroupId | null;
  }): Promise<void> {
    this.placeItem(input.itemId, input.groupId, input.afterItemId);
    this.emit({ kind: 'items' });
  }

  /**
   * P-103 — the whole selection at one drop point, in the order given: each row lands after
   * the one before it, so the selection keeps its relative order.
   */
  async reorderItems(input: {
    boardId: BoardId;
    itemIds: readonly ItemId[];
    groupId: GroupId;
    afterItemId: ItemId | null;
  }): Promise<void> {
    let after = input.afterItemId;
    for (const itemId of input.itemIds) {
      this.placeItem(itemId, input.groupId, after);
      after = itemId;
    }
    this.emit({ kind: 'items' });
  }

  private placeItem(itemId: ItemId, groupId: GroupId, afterItemId: ItemId | null): void {
    const at = this.items.findIndex((item) => item.id === itemId);
    if (at < 0) return;
    const moved = { ...this.items[at]!, groupId };
    this.items.splice(at, 1);
    const siblings = this.items.filter((item) => item.groupId === groupId);
    const anchor = afterItemId ? siblings.find((item) => item.id === afterItemId) : undefined;
    moved.position = anchor ? anchor.position + 0.5 : (siblings[0]?.position ?? 1) - 1;
    this.items.push(moved);
  }

  async createColumn(input: {
    boardId: BoardId;
    type: string;
    title: string;
    afterColumnId: ColumnId | null;
  }): Promise<BoardColumn> {
    const shared = findColumnType(input.type);
    const base = shared?.idPrefix ?? input.type;
    /**
     * P-111: the add-column gallery now names the board it is adding to, and the nested
     * header's `+` names the **subitems** board — so the fixture keeps the two lists apart
     * exactly as the server keeps two boards apart.
     */
    const subitems = String(input.boardId) === String(subitemsBoardIdOf(this.seed.board.id));
    const target = subitems ? this.subitemColumns : this.columns;
    const id = (target.some((column) => column.id === base)
      ? `${base}_${Math.random().toString(36).slice(2, 10)}`
      : base) as ColumnId;
    const after = input.afterColumnId
      ? target.find((column) => column.id === input.afterColumnId)
      : undefined;
    const position = after ? after.position + 0.5 : target.length;
    const column: BoardColumn = {
      id,
      type: input.type,
      title: input.title,
      description: null,
      settings: shared?.defaultSettings ?? {},
      width: null,
      position,
      revision: 'r1',
      aggregation: (shared ? defaultAggregationOf(shared) : 'none') as ColumnAggregation,
    };
    const merged = [...target, column]
      .sort((a, b) => a.position - b.position)
      .map((entry, index) => ({ ...entry, position: index }));
    if (subitems) {
      this.subitemColumns.length = 0;
      this.subitemColumns.push(...merged);
    } else {
      this.columns = merged;
    }
    this.emit({ kind: 'columns' });
    return merged.find((entry) => entry.id === id)!;
  }

  async updateColumn(input: {
    boardId: BoardId;
    columnId: ColumnId;
    revision: string;
    title?: string;
    description?: string | null;
    width?: number;
    settings?: unknown;
    aggregation?: ColumnAggregation;
  }): Promise<BoardColumn> {
    const index = this.columns.findIndex((column) => column.id === input.columnId);
    if (index < 0) throw new Error('unknown column');
    const current = this.columns[index]!;
    // L9 / E8: a stale `revision` fails and writes nothing `[09§A.2]`.
    if (current.revision !== input.revision) throw new Error('conflict');
    const next: BoardColumn = {
      ...current,
      title: input.title ?? current.title,
      description: input.description === undefined ? current.description : input.description,
      width: input.width ?? current.width,
      settings: input.settings === undefined ? current.settings : input.settings,
      aggregation: input.aggregation ?? current.aggregation,
      revision: `r${Number(current.revision.slice(1)) + 1}`,
    };
    this.columns = this.columns.map((column, at) => (at === index ? next : column));
    this.emit({ kind: 'columns' });
    return next;
  }

  async deleteColumn(input: { boardId: BoardId; columnId: ColumnId }): Promise<void> {
    // H4 / L10: `name` can never be deleted.
    if (input.columnId === 'name') throw new ForbiddenError('the name column cannot be deleted');
    const at = this.columns.findIndex((column) => column.id === input.columnId);
    // P-087: the service's delete is a soft one, so the fixture keeps the row too — otherwise
    // the toast's **Undo** would pass here and fail on a real board. `at` is the *index*, not
    // `position`: a seeded board's positions are the real board's and are not 0…n-1.
    if (at >= 0) this.deletedColumns.set(String(input.columnId), { column: this.columns[at]!, at });
    this.columns = this.columns
      .filter((column) => column.id !== input.columnId)
      .map((column, index) => ({ ...column, position: index }));
    this.emit({ kind: 'columns' });
  }

  async restoreColumn(input: { boardId: BoardId; columnId: ColumnId }): Promise<void> {
    const entry = this.deletedColumns.get(String(input.columnId));
    if (!entry) throw new Error('unknown column');
    this.deletedColumns.delete(String(input.columnId));
    this.columns = [...this.columns, { ...entry.column, position: entry.at - 0.5 }]
      .sort((a, b) => a.position - b.position)
      .map((column, index) => ({ ...column, position: index }));
    this.emit({ kind: 'columns' });
  }

  /** `dup__of_<id>` / `Dup. of <title>`, no cells — exactly what the service does (Q47). */
  async duplicateColumn(input: { boardId: BoardId; columnId: ColumnId }): Promise<BoardColumn> {
    const source = this.columns.find((column) => column.id === input.columnId);
    if (!source) throw new Error('unknown column');
    return this.createColumn({
      boardId: input.boardId,
      type: source.type,
      title: `Dup. of ${source.title}`,
      afterColumnId: source.id,
    });
  }

  async moveColumn(input: {
    boardId: BoardId;
    columnId: ColumnId;
    beforeColumnId: ColumnId | null;
  }): Promise<void> {
    if (input.columnId === 'name') throw new ForbiddenError('the name column stays at position 0');
    const ordered = [...this.columns].sort((a, b) => a.position - b.position);
    const moving = ordered.find((column) => column.id === input.columnId);
    if (!moving) return;
    const rest = ordered.filter((column) => column.id !== input.columnId);
    const at = input.beforeColumnId
      ? rest.findIndex((column) => column.id === input.beforeColumnId)
      : rest.length;
    rest.splice(at < 0 ? rest.length : at, 0, moving);
    this.columns = rest.map((column, index) => ({ ...column, position: index }));
    this.emit({ kind: 'columns' });
  }

  async saveViewSettings(input: { viewId: ViewId; settings: TableViewSettings }): Promise<void> {
    // F10: refused for a user who is neither the view owner nor a board owner.
    if (!this.permissions.canSaveView) throw new ForbiddenError();
    this.saved.set(input.viewId, sharedSettings(input.settings));
    // §8.5: saving clears that user's personal state for the view.
    this.personal.delete(input.viewId);
    this.emit({ kind: 'view' });
  }

  async setPersonalState(input: { viewId: ViewId; state: Partial<TableViewSettings> }): Promise<void> {
    this.personal.set(input.viewId, input.state);
  }

  subscribe(listener: (change: BoardChange) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /* ── test helpers (never called by the view) ────────────────────────────── */

  /** The stored per-user overlay for a view — P-268 asserts what the store wrote back. */
  personalStateOf(viewId: ViewId): Partial<TableViewSettings> | undefined {
    return this.personal.get(viewId);
  }

  /** The current row for an item, so tests can assert a write landed. */
  itemById(itemId: ItemId): ItemRow | undefined {
    return this.items.find((item) => item.id === itemId);
  }

  columnById(columnId: ColumnId): BoardColumn | undefined {
    return this.columns.find((column) => column.id === columnId);
  }

  savedSettings(viewId: ViewId): TableViewSettings | undefined {
    return this.saved.get(viewId);
  }
}

/** Removes every rule naming one column, keeping the tree shape (facet counting, §5.4). */
function stripColumn(
  group: FilterGroup | null,
  columnId: ColumnId,
): FilterGroup | null {
  if (!group) return null;
  const rules = group.rules.filter((rule) => rule.columnId !== columnId);
  const groups = group.groups
    .map((child) => stripColumn(child, columnId))
    .filter((child): child is NonNullable<typeof child> => child !== null);
  if (rules.length === 0 && groups.length === 0) return null;
  return { operator: group.operator, rules, groups };
}

export function widthOf(column: BoardColumn): number {
  return columnWidth(column);
}
